import React from 'react';
import { useWaterStore } from '../store/useWaterStore';
import { useAuthStore } from '../store/useAuthStore';
import { Award, Droplet, Flame, Trophy, Star, Lock } from 'lucide-react';

export const Badges: React.FC = () => {
    const { streak, currentAmount, dailyGoal } = useWaterStore();
    const { isGuest } = useAuthStore();

    const badges = [
        {
            id: 'first-sip',
            name: 'First Sip',
            description: 'Start your hydration journey',
            icon: Droplet,
            color: 'text-blue-500',
            bgColor: 'bg-blue-100',
            isUnlocked: currentAmount > 0,
        },
        {
            id: 'goal-reached',
            name: 'Goal Crusher',
            description: 'Reach your daily water goal',
            icon: Star,
            color: 'text-yellow-500',
            bgColor: 'bg-yellow-100',
            isUnlocked: currentAmount >= dailyGoal && dailyGoal > 0,
        },
        {
            id: 'streak-3',
            name: 'On a Roll',
            description: 'Reach a 3-day streak',
            icon: Flame,
            color: 'text-orange-500',
            bgColor: 'bg-orange-100',
            isUnlocked: streak >= 3,
        },
        {
            id: 'streak-7',
            name: 'Hydration Hero',
            description: 'Reach a 7-day streak',
            icon: Award,
            color: 'text-purple-500',
            bgColor: 'bg-purple-100',
            isUnlocked: streak >= 7,
        },
        {
            id: 'streak-30',
            name: 'Water Master',
            description: 'Reach a 30-day streak',
            icon: Trophy,
            color: 'text-indigo-500',
            bgColor: 'bg-indigo-100',
            isUnlocked: streak >= 30,
        },
    ];

    return (
        <div className="p-4">
            <h3 className="text-lg font-semibold mb-4">Your Achievements</h3>
            <div className="grid grid-cols-1 gap-4">
                {badges.map((badge) => (
                    <div
                        key={badge.id}
                        className={`flex items-center p-4 rounded-xl border transition-all ${
                            isGuest && !badge.isUnlocked
                                ? 'bg-gray-100 border-gray-200 opacity-70'
                                : badge.isUnlocked
                                    ? 'bg-white border-gray-200 shadow-sm'
                                    : 'bg-gray-50 border-gray-100 opacity-60 grayscale'
                        }`}
                    >
                        <div className={`p-3 rounded-full ${
                            isGuest && !badge.isUnlocked
                                ? 'bg-gray-200'
                                : badge.isUnlocked
                                    ? badge.bgColor
                                    : 'bg-gray-200'
                        } mr-4`}>
                            {isGuest && !badge.isUnlocked ? (
                                <Lock className="w-6 h-6 text-gray-400" />
                            ) : (
                                <badge.icon className={`w-6 h-6 ${
                                    isGuest && !badge.isUnlocked
                                        ? 'text-gray-400'
                                        : badge.isUnlocked
                                            ? badge.color
                                            : 'text-gray-400'
                                }`} />
                            )}
                        </div>
                        <div>
                            <h4 className={`font-bold ${
                                isGuest && !badge.isUnlocked
                                    ? 'text-gray-600'
                                    : badge.isUnlocked
                                        ? 'text-gray-800'
                                        : 'text-gray-500'
                            }`}>
                                {badge.name}
                            </h4>
                            <p className="text-sm text-gray-500">{badge.description}</p>
                            
                            {/* Show sign-in prompt for locked badges in guest mode */}
                            {isGuest && !badge.isUnlocked && (
                                <p className="text-xs text-blue-600 mt-1 font-medium">
                                    Sign in to unlock this badge
                                </p>
                            )}
                        </div>
                        {badge.isUnlocked && (
                            <div className="ml-auto">
                                <span className="text-xs font-bold text-green-600 bg-green-100 px-2 py-1 rounded-full">
                                    Unlocked
                                </span>
                            </div>
                        )}
                    </div>
                ))}
            </div>
            
            {/* Show guest mode message at the bottom */}
            {isGuest && (
                <div className="mt-4 text-center">
                    <p className="text-sm text-gray-600">
                        Sign in to save your achievements and unlock all badges
                    </p>
                    <button
                        onClick={() => (window.location.href = '/login')}
                        className="mt-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 text-sm"
                    >
                        Sign In
                    </button>
                </div>
            )}
        </div>
    );
};
